import { useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY = 'autopod-followed-shows';
const NOTIFIED_KEY = 'autopod-notified-shows';

export interface FollowedShow {
  showId: number;
  showTitle: string;
  streamId: number;
  streamName: string;
  image?: string;
}

export interface UseShowNotificationsReturn {
  /** Whether notifications are supported */
  isSupported: boolean;
  /** Current notification permission status */
  permission: NotificationPermission | 'unsupported';
  /** List of followed shows */
  followedShows: FollowedShow[];
  /** Check if a show is followed */
  isFollowing: (showId: number) => boolean;
  /** Follow a show for notifications */
  followShow: (show: FollowedShow) => Promise<boolean>;
  /** Unfollow a show */
  unfollowShow: (showId: number) => void;
  /** Request notification permission */
  requestPermission: () => Promise<boolean>;
}

function loadFollowedShows(): FollowedShow[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveFollowedShows(shows: FollowedShow[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(shows));
  } catch {
    // Ignore storage errors
  }
}

function getNotifiedToday(): Set<number> {
  try {
    const stored = localStorage.getItem(NOTIFIED_KEY);
    if (!stored) return new Set();

    const data = JSON.parse(stored);
    const today = new Date().toDateString();

    // Clear if from a different day
    if (data.date !== today) {
      localStorage.removeItem(NOTIFIED_KEY);
      return new Set();
    }

    return new Set(data.showIds);
  } catch {
    return new Set();
  }
}

function markNotifiedToday(showId: number): void {
  try {
    const notified = getNotifiedToday();
    notified.add(showId);

    localStorage.setItem(NOTIFIED_KEY, JSON.stringify({
      date: new Date().toDateString(),
      showIds: Array.from(notified),
    }));
  } catch {
    // Ignore storage errors
  }
}

async function sendNotification(show: FollowedShow): Promise<void> {
  if (Notification.permission !== 'granted') return;

  // Check if already notified today for this show
  const notified = getNotifiedToday();
  if (notified.has(show.showId)) return;

  try {
    const notification = new Notification(`${show.showTitle} is Live!`, {
      body: `Tap to listen on ${show.streamName}`,
      icon: show.image,
      badge: show.image,
      tag: `live-show-${show.showId}`,
    } as NotificationOptions);

    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    markNotifiedToday(show.showId);
  } catch (error) {
    console.error('Failed to send notification:', error);
  }
}

export function useShowNotifications(
  streamId: number | null,
  currentlyLiveShowId: number | null,
  isPWAMode: boolean
): UseShowNotificationsReturn {
  const [isSupported] = useState(() => {
    return typeof window !== 'undefined' && 'Notification' in window;
  });

  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(() => {
    if (!isSupported) return 'unsupported';
    return Notification.permission;
  });

  const [followedShows, setFollowedShows] = useState<FollowedShow[]>(() => loadFollowedShows());

  // Track last notified show to avoid duplicate notifications
  const lastNotifiedRef = useRef<number | null>(null);

  // Check for live shows and send notifications
  useEffect(() => {
    if (!isPWAMode || !isSupported || permission !== 'granted') return;
    if (!currentlyLiveShowId || !streamId) return;

    // Find if the currently live show is one we follow
    const followedShow = followedShows.find(
      s => s.showId === currentlyLiveShowId && s.streamId === streamId
    );

    if (followedShow && lastNotifiedRef.current !== currentlyLiveShowId) {
      lastNotifiedRef.current = currentlyLiveShowId;
      sendNotification(followedShow);
    }
  }, [isPWAMode, isSupported, permission, currentlyLiveShowId, streamId, followedShows]);

  // Reset last notified when show changes
  useEffect(() => {
    if (currentlyLiveShowId === null) {
      lastNotifiedRef.current = null;
    }
  }, [currentlyLiveShowId]);

  const isFollowing = useCallback((showId: number): boolean => {
    return followedShows.some(s => s.showId === showId);
  }, [followedShows]);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) return false;

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    } catch {
      return false;
    }
  }, [isSupported]);

  const followShow = useCallback(async (show: FollowedShow): Promise<boolean> => {
    // Request permission if not already granted
    if (permission === 'default') {
      const granted = await requestPermission();
      if (!granted) return false;
    } else if (permission !== 'granted') {
      return false;
    }

    setFollowedShows(prev => {
      // Don't add duplicates
      if (prev.some(s => s.showId === show.showId)) return prev;

      const updated = [...prev, show];
      saveFollowedShows(updated);
      return updated;
    });

    return true;
  }, [permission, requestPermission]);

  const unfollowShow = useCallback((showId: number): void => {
    setFollowedShows(prev => {
      const updated = prev.filter(s => s.showId !== showId);
      saveFollowedShows(updated);
      return updated;
    });
  }, []);

  return {
    isSupported,
    permission,
    followedShows,
    isFollowing,
    followShow,
    unfollowShow,
    requestPermission,
  };
}
